import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldAlert, Loader2, AlertTriangle } from 'lucide-react';
import { TopBar } from '@/components/layout/TopBar';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { describeShopSupportAccessError, readGrantedShop, SCOPE_LABEL, type ShopSupportScope } from '@/lib/support/shopSupportAccess';

/**
 * Core-100 R5 CORE-BJ / OD-49 — the operator's READ-ONLY view of a shop that approved support access.
 *
 * Everything shown comes from `support_read_granted_shop`, which re-checks the platform switch, the operator's
 * designation and the shop's approval on every call, records each read for the shop to see, and returns only an
 * allowlisted projection (customers redacted unless the shop's approval names them; never phone, email, address,
 * vehicle identifiers or notes). There are no controls that change anything here — the server has no such path.
 */
interface TicketRow {
  id: string;
  ticket_number: string | null;
  status: string | null;
  type: string | null;
  scheduled_start_time: string | null;
  due_out: string | null;
  customer_name: string | null;
  customer_redacted: boolean;
}

const fmtRemaining = (s: number | null) => {
  if (s === null) return '';
  const v = Math.max(0, s);
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`;
};

export default function SupportGrantedShopView() {
  const navigate = useNavigate();
  const { supportSession, timeRemaining, endSupportSession } = useSupportMode();
  const [overview, setOverview] = useState<Record<string, unknown> | null>(null);
  const [tickets, setTickets] = useState<TicketRow[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const sessionId = supportSession?.sessionId ?? null;

  const load = useCallback(async () => {
    if (!sessionId) return;
    try {
      const [o, t] = [await readGrantedShop(sessionId, 'OVERVIEW'), await readGrantedShop(sessionId, 'TICKETS')];
      setOverview(o);
      setTickets(((t as { tickets?: TicketRow[] }).tickets) ?? []);
      setRefusal(null);
    } catch (error) {
      setOverview(null);
      setTickets(null);
      setRefusal(describeShopSupportAccessError(error as { message?: string }) ?? 'The shop could not be read. Nothing is shown.');
    }
  }, [sessionId]);

  useEffect(() => { void load(); }, [load]);

  const scope = (overview?.scope ?? supportSession?.grantScope) as ShopSupportScope | undefined;
  const shop = overview?.shop as { name?: string; time_zone?: string } | undefined;

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <div className="container mx-auto p-6 max-w-5xl space-y-4">
        {!sessionId ? (
          <Alert data-testid="granted-shop-no-session">
            <AlertDescription>No shop-authorised support session is open. Open one from Support · Shops after the shop approves it.</AlertDescription>
          </Alert>
        ) : (
          <>
            <Alert variant="destructive" data-testid="granted-shop-indicator">
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  Support access to <strong>{shop?.name ?? supportSession?.targetShop?.name ?? 'this shop'}</strong> — read-only,
                  approved by the shop{scope ? ` · ${SCOPE_LABEL[scope]}` : ''}. Every read is recorded and visible to the shop.
                  {timeRemaining !== null && <> Ends in <span data-testid="granted-shop-countdown">{fmtRemaining(timeRemaining)}</span>.</>}
                </span>
                <Button size="sm" variant="outline" onClick={async () => { await endSupportSession(); navigate('/support/shops'); }}>
                  End access
                </Button>
              </AlertDescription>
            </Alert>

            {refusal ? (
              <Alert variant="destructive" data-testid="granted-shop-refusal">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{refusal}</AlertDescription>
              </Alert>
            ) : overview === null ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Card>
                  <CardHeader><CardTitle>Overview</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <p>Time zone: {shop?.time_zone ?? 'unknown'}</p>
                    <p>Bays: {Array.isArray(overview.bays) ? overview.bays.length : 0}</p>
                    <p>Members by role: {Object.entries((overview.members_by_role ?? {}) as Record<string, number>)
                      .map(([r, n]) => `${r} ${n}`).join(' · ') || 'none'}</p>
                    <p>Tickets by status: {Object.entries((overview.tickets_by_status ?? {}) as Record<string, number>)
                      .map(([s, n]) => `${s} ${n}`).join(' · ') || 'none'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle>Open tickets</CardTitle></CardHeader>
                  <CardContent>
                    {!tickets?.length ? (
                      <p className="text-sm text-muted-foreground">No open tickets.</p>
                    ) : (
                      <div className="space-y-1" data-testid="granted-shop-tickets">
                        {tickets.map((t) => (
                          <div key={t.id} className="flex items-center justify-between text-sm border-b border-border py-1">
                            <span>{t.ticket_number ?? t.id} · {t.type ?? ''}</span>
                            <span className="text-muted-foreground">
                              {t.customer_redacted ? <Badge variant="outline">Customer hidden</Badge> : t.customer_name}
                            </span>
                            <Badge variant="secondary">{t.status ?? 'unknown'}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
